import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Crown, Lock, Save, RotateCcw, Type, Eye, Sparkles, ImageIcon, Upload, X, Hash } from "lucide-react";
import { InlineFormError } from "@/components/ui/inline-form-error";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import { DEFAULT_BRANDING, type Branding } from "@/hooks/use-branding";
import { PageShell, PageStat } from "@/components/page-shell";
import { validateAndReadAsset, ASSET_RULES, type AssetKind } from "@/lib/branding-assets";

export default function AdminBrandingPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  const isSuperAdmin = user?.role === "super_admin";

  // Defence-in-depth: the route is gated in App.tsx and the API requires
  // super_admin, but if a non-super-admin lands here directly we bounce
  // them away instead of rendering the form (server still rejects on PUT).
  useEffect(() => {
    if (user && !isSuperAdmin) {
      setLocation("/dashboard");
    }
  }, [user, isSuperAdmin, setLocation]);

  const { data, isLoading } = useQuery<Partial<Branding> & { version?: number; updatedAt?: string } | null>({
    queryKey: ["/api/branding"],
    enabled: isSuperAdmin,
  });

  const [productName, setProductName] = useState("");
  const [shortName, setShortName] = useState("");
  const [tagline, setTagline] = useState("");
  const [poweredBy, setPoweredBy] = useState("");

  // Asset state — `current` holds the persisted data URL (or null). A fresh
  // file selection replaces it; clearing sets it back to null explicitly.
  const [lightLogo, setLightLogo] = useState<string | null>(null);
  const [favicon, setFavicon] = useState<string | null>(null);
  const [lightLogoError, setLightLogoError] = useState<string | null>(null);
  const [faviconError, setFaviconError] = useState<string | null>(null);
  const lightLogoRef = useRef<HTMLInputElement>(null);
  const faviconRef   = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!data) return;
    setProductName(data.productName ?? "");
    setShortName(data.shortName ?? "");
    setTagline(data.tagline ?? "");
    setPoweredBy(data.poweredBy ?? "");
    // Populate persisted asset values so they survive a Save without re-picking
    setLightLogo((data as any).lightLogo ?? null);
    setFavicon((data as any).favicon ?? null);
  }, [data]);

  // ── File picker handler ──────────────────────────────────────────────────
  const handleAssetFile = async (kind: AssetKind, file: File) => {
    const setVal   = kind === "lightLogo" ? setLightLogo : setFavicon;
    const setError = kind === "lightLogo" ? setLightLogoError : setFaviconError;
    const rule = ASSET_RULES[kind];

    setError(null);
    const result = await validateAndReadAsset(file, rule);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setVal(result.dataUrl);
  };

  const saveMut = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("PUT", "/api/branding", {
        productName: productName.trim(),
        shortName: shortName.trim(),
        tagline: tagline.trim(),
        poweredBy: poweredBy.trim(),
        // Send current data URLs (persisted or newly picked). null = clear.
        lightLogo: lightLogo ?? null,
        favicon:   favicon   ?? null,
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/branding"] });
      toast({ title: "Branding updated", description: "The new branding is now live across the app." });
    },
    onError: (err: Error) =>
      toast({ title: "Could not save branding", description: err.message, variant: "destructive" }),
  });

  const resetMut = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("DELETE", "/api/branding");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/branding"] });
      setProductName("");
      setShortName("");
      setTagline("");
      setPoweredBy("");
      setLightLogo(null);
      setFavicon(null);
      setLightLogoError(null);
      setFaviconError(null);
      if (lightLogoRef.current) lightLogoRef.current.value = "";
      if (faviconRef.current)   faviconRef.current.value   = "";
      toast({ title: "Branding reset to defaults" });
    },
    onError: (err: Error) =>
      toast({ title: "Could not reset", description: err.message, variant: "destructive" }),
  });

  if (!user || !isSuperAdmin) {
    return null;
  }

  // ── Required-asset gate ──────────────────────────────────────────────────
  // Validate presence before the mutation fires so errors appear inline next
  // to the relevant field instead of in a generic toast.
  function handleSave() {
    let blocked = false;
    if (!lightLogo) {
      setLightLogoError("Light logo is required.");
      blocked = true;
    }
    if (!favicon) {
      setFaviconError("Favicon is required.");
      blocked = true;
    }
    if (blocked) return;
    saveMut.mutate();
  }

  const filledFields = [productName, shortName, tagline, poweredBy].filter((v) => v.trim().length > 0).length;
  const isPending = isLoading || saveMut.isPending;

  return (
    <PageShell
      className="max-w-4xl"
      eyebrow={
        <Link href="/admin">
          <span className="inline-flex items-center gap-1 cursor-pointer hover:text-foreground" data-testid="link-back-admin">
            <ArrowLeft className="w-3 h-3" /> Master Admin
          </span>
        </Link>
      }
      title={
        <span className="flex items-center gap-2" data-testid="text-branding-title">
          <Crown className="w-6 h-6 text-amber-600" /> Branding
        </span>
      }
      subtitle="Edit the product name, footer text, and visual assets shown across the app. Changes apply to every tenant immediately."
      actions={
        <Badge variant="outline" className="text-[10px] gap-1" data-testid="badge-internal-branding">
          <Lock className="w-3 h-3" /> Super-admin only
        </Badge>
      }
      stats={
        <>
          <PageStat label="Product name" value={productName.trim() || DEFAULT_BRANDING.productName} hint="Tab title & headers" icon={<Type className="w-4 h-4" />} />
          <PageStat label="Short name" value={shortName.trim() || DEFAULT_BRANDING.shortName} hint="Sidebar logo" icon={<Sparkles className="w-4 h-4" />} />
          <PageStat label="Fields filled" value={`${filledFields}/4`} hint={filledFields === 4 ? "Fully customised" : "Defaults will be used"} icon={<Eye className="w-4 h-4" />} />
          <PageStat label="Scope" value="All tenants" hint="Live across the platform" icon={<Crown className="w-4 h-4 text-amber-600" />} />
        </>
      }
    >
      {/* ── Branding version status — read-only, updates immediately after save ── */}
      <div
        className="flex flex-wrap items-center gap-x-6 gap-y-1 rounded-md border border-border bg-muted/40 px-4 py-2.5 text-sm"
        data-testid="branding-version-status"
      >
        <span className="flex items-center gap-1.5">
          <Hash className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />
          <span className="text-muted-foreground">Branding Version:</span>
          <span className="font-mono font-semibold" data-testid="branding-version-value">
            {data?.version != null ? `v${data.version}` : isLoading ? "…" : "—"}
          </span>
        </span>
        <span className="flex items-center gap-1.5 text-muted-foreground">
          <span>Last Updated:</span>
          <span data-testid="branding-version-updated">
            {data?.updatedAt
              ? new Date(data.updatedAt).toLocaleString()
              : isLoading ? "…" : "Not yet saved"}
          </span>
        </span>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Product identity</CardTitle>
          <CardDescription>
            Empty fields fall back to the platform defaults shown in grey.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="brand-product-name">Product name</Label>
            <Input
              id="brand-product-name"
              value={productName}
              onChange={(e) => setProductName(e.target.value)}
              placeholder={DEFAULT_BRANDING.productName}
              maxLength={120}
              disabled={isPending}
              data-testid="input-brand-product-name"
            />
            <p className="text-xs text-muted-foreground">
              Shown in the browser tab title, login page, footers, and admin headers.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="brand-short-name">Short name (logo)</Label>
            <Input
              id="brand-short-name"
              value={shortName}
              onChange={(e) => setShortName(e.target.value)}
              placeholder={DEFAULT_BRANDING.shortName}
              maxLength={32}
              disabled={isPending}
              data-testid="input-brand-short-name"
            />
            <p className="text-xs text-muted-foreground">
              The compact label used in the sidebar logo and chat widget header.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="brand-tagline">Tagline</Label>
            <Input
              id="brand-tagline"
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              placeholder={DEFAULT_BRANDING.tagline}
              maxLength={200}
              disabled={isPending}
              data-testid="input-brand-tagline"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="brand-powered-by">Powered-by line</Label>
            <Input
              id="brand-powered-by"
              value={poweredBy}
              onChange={(e) => setPoweredBy(e.target.value)}
              placeholder={DEFAULT_BRANDING.poweredBy}
              maxLength={120}
              disabled={isPending}
              data-testid="input-brand-powered-by"
            />
          </div>

          <InlineFormError error={saveMut.isError ? saveMut.error : null} data-testid="error-save-branding" />
          <div className="flex items-center gap-2 pt-2">
            <Button
              onClick={handleSave}
              disabled={saveMut.isPending}
              data-testid="button-save-branding"
            >
              <Save className="w-4 h-4 mr-1" />
              {saveMut.isPending ? "Saving\u2026" : "Save changes"}
            </Button>
            <Button
              variant="outline"
              onClick={() => resetMut.mutate()}
              disabled={resetMut.isPending}
              data-testid="button-reset-branding"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset to defaults
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Image assets ──────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ImageIcon className="w-4 h-4" /> Image assets
          </CardTitle>
          <CardDescription>
            Upload a light-mode logo variant and a browser favicon. Backend enforces type and size — client checks mirror those rules for instant feedback.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* lightLogo */}
          <AssetField
            id="brand-light-logo"
            label="Light logo"
            hint="PNG or JPEG · max 200 KB · used on dark sidebar backgrounds"
            accept={ASSET_RULES.lightLogo.accept}
            currentDataUrl={lightLogo}
            error={lightLogoError}
            inputRef={lightLogoRef}
            disabled={isPending}
            testPrefix="light-logo"
            onFileChange={(file) => handleAssetFile("lightLogo", file)}
            onClear={() => {
              setLightLogo(null);
              setLightLogoError(null);
              if (lightLogoRef.current) lightLogoRef.current.value = "";
            }}
          />

          {/* favicon */}
          <AssetField
            id="brand-favicon"
            label="Favicon"
            hint="PNG or ICO · max 32 KB · shown in browser tabs"
            accept={ASSET_RULES.favicon.accept}
            currentDataUrl={favicon}
            error={faviconError}
            inputRef={faviconRef}
            disabled={isPending}
            testPrefix="favicon"
            onFileChange={(file) => handleAssetFile("favicon", file)}
            onClear={() => {
              setFavicon(null);
              setFaviconError(null);
              if (faviconRef.current) faviconRef.current.value = "";
            }}
          />

        </CardContent>
      </Card>

      {/* ── Live preview ─────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Live preview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p data-testid="preview-product-name">
            <span className="text-muted-foreground">Product name:</span>{" "}
            <strong>{productName.trim() || DEFAULT_BRANDING.productName}</strong>
          </p>
          <p data-testid="preview-short-name">
            <span className="text-muted-foreground">Short name:</span>{" "}
            <strong>{shortName.trim() || DEFAULT_BRANDING.shortName}</strong>
          </p>
          <p data-testid="preview-tagline">
            <span className="text-muted-foreground">Tagline:</span>{" "}
            {tagline.trim() || DEFAULT_BRANDING.tagline}
          </p>
          <p data-testid="preview-powered-by">
            <span className="text-muted-foreground">Powered by:</span>{" "}
            {poweredBy.trim() || DEFAULT_BRANDING.poweredBy}
          </p>
          <div className="flex flex-wrap gap-4 mt-2">
            {lightLogo && (
              <div className="flex flex-col items-center gap-1">
                <span className="text-xs text-muted-foreground">Light logo</span>
                <img
                  src={lightLogo}
                  alt="Light logo preview"
                  className="h-8 rounded border bg-slate-800 p-1"
                  data-testid="preview-img-light-logo"
                />
              </div>
            )}
            {favicon && (
              <div className="flex flex-col items-center gap-1">
                <span className="text-xs text-muted-foreground">Favicon</span>
                <img
                  src={favicon}
                  alt="Favicon preview"
                  className="h-8 w-8 rounded border"
                  data-testid="preview-img-favicon"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </PageShell>
  );
}

// ── Sub-component: reusable asset field ────────────────────────────────────

interface AssetFieldProps {
  id: string;
  label: string;
  hint: string;
  accept: string;
  currentDataUrl: string | null;
  error: string | null;
  inputRef: React.RefObject<HTMLInputElement>;
  disabled: boolean;
  testPrefix: string;
  onFileChange: (file: File) => void;
  onClear: () => void;
}

function AssetField({
  id,
  label,
  hint,
  accept,
  currentDataUrl,
  error,
  inputRef,
  disabled,
  testPrefix,
  onFileChange,
  onClear,
}: AssetFieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <p className="text-xs text-muted-foreground">{hint}</p>
      <div className="flex items-start gap-3">
        {/* ── Image preview thumbnail ── */}
        <div
          className="w-16 h-16 rounded-md border bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden"
          data-testid={`preview-thumb-${testPrefix}`}
        >
          {currentDataUrl ? (
            <img
              src={currentDataUrl}
              alt={`${label} preview`}
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <ImageIcon className="w-6 h-6 text-muted-foreground/50" />
          )}
        </div>

        {/* ── Controls ── */}
        <div className="flex-1 space-y-2">
          {/* Hidden file input */}
          <input
            id={id}
            ref={inputRef}
            type="file"
            accept={accept}
            className="sr-only"
            disabled={disabled}
            data-testid={`input-file-${testPrefix}`}
            aria-label={`Upload ${label}`}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onFileChange(file);
            }}
          />
          <div className="flex items-center gap-2">
            {/* Visible button that triggers the hidden input */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled}
              data-testid={`button-pick-${testPrefix}`}
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {currentDataUrl ? "Replace" : "Choose file"}
            </Button>
            {currentDataUrl && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                data-testid={`button-clear-${testPrefix}`}
                aria-label={`Remove ${label}`}
                onClick={onClear}
              >
                <X className="w-3.5 h-3.5 mr-1" />
                Remove
              </Button>
            )}
          </div>
          {error && (
            <p
              className="text-xs text-destructive"
              role="alert"
              data-testid={`error-${testPrefix}`}
            >
              {error}
            </p>
          )}
          {!error && currentDataUrl && (
            <p className="text-xs text-muted-foreground" data-testid={`status-${testPrefix}`}>
              ✓ Asset loaded
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
